import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, reactive, ref, watch } from 'vue';
import {
  AuthorityType,
  CompanyRegistrationModel,
  ClientType,
  Permission,
  CompanyQuestionnaire,
  IndividualQuestionnaire,
  Occupation,
} from 'ah-api-gateways';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { FormDefinition, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { tradingDetailsFM, tradingDetailsFormToModel } from '@/app/helpers/registration/forms';
import { getChildModel, setState, updateModel } from 'ah-common-lib/src/form/helpers';
import { useSettingsStore } from '@/app/store/settingsModule';
import {
  sourceOfFundOptions,
  occupationOptions,
  SourceOfFunds,
  sourceOfFundsLabels,
  occupationLabels,
} from 'ah-common-lib/src/helpers/questionnaire';
import { useAuthStore } from '@/app/store/authModule';
import OBOSettingsForm from './OBOSettingsForm.vue';
import { getServices } from '@/app/services';
import { forkJoin } from 'rxjs';
import IconAlertCircle from 'ah-common-lib/src/icons/components/IconAlertCircle.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TradingProfileForm',
  props: {
    model: null
  },
  emits: ["proceed", "update:model"],
  setup(__props: any, { emit }: { emit: ({
  /**
   * 'proceed' - Emitted when valid form is submitted
   */
  (e: 'proceed', value: void): void;
  /**
   * 'update:model' - Emitted when any changes on `model` are done
   */
  (e: 'update:model', value: Partial<CompanyRegistrationModel>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { model: Partial<CompanyRegistrationModel> };

const requestManager = useRequestManager().manager;

const services = getServices();





const settingsStore = useSettingsStore();

const authStore = useAuthStore();

const permissions = ref<Permission[]>([
  { authority: AuthorityType.PAY_ON_BEHALF_OF, allow: true },
  { authority: AuthorityType.TRADE_ON_BEHALF_OF, allow: true },
]);

const oboValidation = ref<FormValidation<Permission[]>>();

const allowedToSetPermissions = computed(
  () => props.model.clientType === ClientType.INDIVIDUAL || props.model.individuals!.find((i) => i.applicant)?.owner
);

const isCompany = computed(() => authStore.isCompanyClient);

const calculatedModel = computed(() => ({
  ...props.model,
  permissions,
  questionnaire: tradingDetailsFormToModel(tradingProfileFM.form, isCompany.value),
}));

const validation = computed(() => {
  return {
    $model: calculatedModel,
    $invalid: !!tradingProfileFM.validation?.$invalid || !!oboValidation.value?.$invalid,
    $dirty: !!tradingProfileFM.validation?.$dirty || !!oboValidation.value?.$dirty,
  };
});

const clientId = computed(() => authStore.loggedInIdentity!.client!.id);

const tradingProfileFM = reactive<FormDefinition>({ form: tradingDetailsFM(isCompany.value), validation: null });

const emirClassificationTooltip = reactive<{
  title: string;
  html: Boolean;
  container: string;
  customClass: string;
  trigger: string;
}>({
  title:
    '<p><b>Financial Counterparties (FCs):</b> These include banks, investment firms, credit institutions, insurance companies, pension funds, fund management companies.</p><p><b>Non-financial counterparties (NFCs):</b> These include all who do not fall into the definition of a FC.</p>',
  html: true,
  container: 'emirClassificationForm',
  customClass: 'emir-classification-tooltip',
  trigger: 'hover',
});

onBeforeMount(() => {
  settingsStore.loadCountries().then(() => {
    const model = getChildModel(tradingProfileFM.form, 'beneficiaryCountries');
    if (model) {
      setState(
        model,
        'options',
        settingsStore.allCountries.map((country) => ({ label: country.name, value: country.cc }))
      );
    }
  });

  settingsStore.loadTradeableCurrencies().then(() => {
    ['sellCurrencies', 'buyCurrencies', 'reportingCurrency'].forEach((field) => {
      const model = getChildModel(tradingProfileFM.form, field);
      if (model) {
        setState(model, 'options', settingsStore.currenciesAsOptions(false));
      }
    });
  });
});

function savePermissions() {
  if (!allowedToSetPermissions.value) {
    return Promise.resolve(null);
  }

  return requestManager
    .cancelAndNew(
      'savePermissions',
      forkJoin(permissions.value.map((p) => services.authz.setClientsPermissions(clientId.value, p)))
    )
    .toPromise();
}

function saveComplianceCase() {
  if (calculatedModel.value.questionnaire.maxExpectedVolume === Infinity) {
    calculatedModel.value.questionnaire.maxExpectedVolume = undefined;
  }

  return requestManager
    .cancelAndNew(
      'updateComplianceCase',
      services.compliance.updateClientComplianceCase(authStore.loggedInIdentity!.client!.id, {
        questionnaire: { ...calculatedModel.value.questionnaire },
      })
    )
    .toPromise();
}

function proceed() {
  savePermissions()
    .then(saveComplianceCase)
    .then(() => {
      emit('update:model', calculatedModel.value);
      emit('proceed');
    });
}

watch(
  () => [tradingProfileFM.form.sourceOfFundsSelection, tradingProfileFM.form.occupationSelection],
  () => {
    const sourceOfFundsField = getChildModel(tradingProfileFM.form, 'sourceOfFunds');
    const occupationField = getChildModel(tradingProfileFM.form, 'occupation');

    if (sourceOfFundsField) {
      const isOther = tradingProfileFM.form.sourceOfFundsSelection === sourceOfFundsLabels.OTHER;
      setState(sourceOfFundsField, 'required', isOther);
      setState(sourceOfFundsField, 'hidden', !isOther);
      if (tradingProfileFM.validation?.sourceOfFunds) {
        tradingProfileFM.validation.sourceOfFunds.$reset();
      }
    }
    if (occupationField) {
      const isOther = tradingProfileFM.form.occupationSelection === occupationLabels.OTHER;
      setState(occupationField, 'required', isOther);
      setState(occupationField, 'hidden', !isOther);
      if (tradingProfileFM.validation?.occupation) {
        tradingProfileFM.validation.occupation.$reset();
      }
    }
  },
  { immediate: true }
);

watch(
  () => props.model,
  () => {
    updateModel(tradingProfileFM.form, props.model.questionnaire ?? {});

    if (props.model.questionnaire?.minExpectedVolume === 10000001) {
      tradingProfileFM.form.maxExpectedVolume = Infinity;
    }

    const sourceOfFunds = (props.model.questionnaire as CompanyQuestionnaire)?.sourceOfFunds ?? null;
    if (sourceOfFunds) {
      if (!sourceOfFundOptions.find((o) => o.value === sourceOfFunds)) {
        tradingProfileFM.form.sourceOfFundsSelection = SourceOfFunds.OTHER;
      } else {
        tradingProfileFM.form.sourceOfFundsSelection = sourceOfFunds;
        tradingProfileFM.form.sourceOfFunds = '';
      }
    }

    const occupation = (props.model.questionnaire as IndividualQuestionnaire)?.occupation ?? null;
    if (occupation) {
      if (!occupationOptions.find((o) => o.value === occupation)) {
        tradingProfileFM.form.occupationSelection = Occupation.OTHER;
      } else {
        tradingProfileFM.form.occupationSelection = occupation;
        tradingProfileFM.form.occupation = '';
      }
    }
  },
  { immediate: true }
);

return { __sfc: true,requestManager, services, props, emit, settingsStore, authStore, permissions, oboValidation, allowedToSetPermissions, isCompany, calculatedModel, validation, clientId, tradingProfileFM, emirClassificationTooltip, savePermissions, saveComplianceCase, proceed, OBOSettingsForm, IconAlertCircle }
}

})